import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Badge, Button, Modal, Form, Alert, Dropdown } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader.jsx';

export default function UsersPage() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState({ email: '', display_name: '', password: '', role_ids: [] });
  const [error, setError] = useState(null);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get('/users').then(r => r.data.items) });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get('/roles').then(r => r.data.items) });

  const create = useMutation({
    mutationFn: (body) => api.post('/users', body).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setShow(false); setDraft({ email: '', display_name: '', password: '', role_ids: [] }); },
    onError: (e) => setError(e.response?.data?.message || JSON.stringify(e.response?.data))
  });

  const setUserRoles = useMutation({
    mutationFn: ({ id, role_ids }) => api.put(`/users/${id}/roles`, { role_ids }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] })
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }) => api.post(`/users/${id}/${active ? 'activate' : 'deactivate'}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] })
  });

  const toggleRole = (user, roleId) => {
    const has = user.roles?.some(r => r.id === roleId);
    const role_ids = has
      ? user.roles.filter(r => r.id !== roleId).map(r => r.id)
      : [...(user.roles || []).map(r => r.id), roleId];
    setUserRoles.mutate({ id: user.id, role_ids });
  };

  return (
    <>
      <PageHeader title="Benutzer" subtitle="Admin-Zugang zum Kontor"
        actions={<Button onClick={() => { setShow(true); setError(null); }}><Icon icon="tabler:plus" className="me-1" />Neuer Benutzer</Button>} />

      <Card className="card-flat">
        <Card.Body className="p-0">
          <Table hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr><th>E-Mail</th><th>Name</th><th>Rollen</th><th>MFA</th><th>Status</th><th>Letzter Login</th><th></th></tr>
            </thead>
            <tbody>
              {(users.data || []).map(u => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.display_name}</td>
                  <td>
                    {(u.roles || []).map(r => <Badge key={r.id} bg="" className="badge-soft-primary me-1">{r.name}</Badge>)}
                  </td>
                  <td>{u.mfa_enabled
                    ? <Badge bg="" className="badge-soft-success">aktiv</Badge>
                    : <Badge bg="" className="badge-soft-warning">aus</Badge>}</td>
                  <td>{u.is_active
                    ? (u.locked_until && new Date(u.locked_until) > new Date()
                        ? <Badge bg="" className="badge-soft-warning">gesperrt</Badge>
                        : <Badge bg="" className="badge-soft-success">aktiv</Badge>)
                    : <Badge bg="" className="badge-soft-secondary">inaktiv</Badge>}</td>
                  <td className="text-muted small">{u.last_login_at ? new Date(u.last_login_at).toLocaleString('de-DE') : '—'}</td>
                  <td className="text-end pe-3">
                    <Dropdown align="end">
                      <Dropdown.Toggle variant="outline-secondary" size="sm">…</Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Header>Rollen</Dropdown.Header>
                        {(roles.data || []).map(r => {
                          const has = u.roles?.some(ur => ur.id === r.id);
                          return (
                            <Dropdown.Item key={r.id} onClick={() => toggleRole(u, r.id)}>
                              {has ? <Icon icon="tabler:check" className="me-2" /> : <span style={{ display: 'inline-block', width: 22 }} />}
                              {r.name}
                            </Dropdown.Item>
                          );
                        })}
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => setActive.mutate({ id: u.id, active: !u.is_active })}>
                          {u.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Neuer Benutzer</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>E-Mail *</Form.Label>
            <Form.Control type="email" required value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Anzeigename *</Form.Label>
            <Form.Control required value={draft.display_name}
              onChange={(e) => setDraft({ ...draft, display_name: e.target.value })} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Rollen</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {(roles.data || []).map(r => (
                <Form.Check key={r.id} type="checkbox" id={`r-${r.id}`} label={r.name}
                  checked={draft.role_ids.includes(r.id)}
                  onChange={(e) => setDraft(d => ({
                    ...d,
                    role_ids: e.target.checked ? [...d.role_ids, r.id] : d.role_ids.filter(x => x !== r.id)
                  }))} />
              ))}
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>Initial-Passwort *</Form.Label>
            <Form.Control type="password" required value={draft.password}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
            <Form.Text className="text-muted">Min. 12 Zeichen, je 1× Klein/Groß/Zahl/Sonderzeichen. Muss beim ersten Login geändert werden.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShow(false)}>Abbrechen</Button>
          <Button onClick={() => create.mutate(draft)} disabled={create.isLoading}>Erstellen</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
